import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client.js';
import CharacterPicker from './CharacterPicker.jsx';
import MatchControl from './MatchControl.jsx';
import OverlayPreview from './OverlayPreview.jsx';

// Tekken 8's In-Match card, for accounts with either of two admin opt-ins.
//
// Show on overlay (every account with the card): the set the overlays show.
// Auto is start.gg's stream queue, as always; a pick (state.match.onStream,
// any unfinished set of the event, listed by /api/packages/[slug]/on-stream)
// is shown in its place until that set is over, when the server goes back
// to the queue and this card clears the pick (functions/_lib/live.js).
//
// The character picker (users.portrait_masks_enabled): the game draws each
// player's portrait in its top corner; picking the two characters lets the
// overlay run its labels on under those portraits, their outlines cut out of
// the labels (public/overlay/in_match.html, .cut). Each side picks from a
// grid of the game's select-screen thumbnails (CharacterPicker). The picks
// are stored for one set (state.match.characters.setId, the set on the
// overlay when picked) and reach the overlay only while that set is on
// stream, so a stale pick never cuts the wrong outline into the next
// match's labels.
// With the match control on, they also go into each game reported;
// characters.swapped says the players sit the other way round from
// start.gg's order.
// Under the pickers, the HUD fit (state.match.hudFit): the overlay's holes
// sit where a 1:1 1080p capture draws the portraits, and a rig whose game
// picture is scaled down and centred in OBS draws them further in and
// lower (cgc's rig, 2026-10-17: about 97%). Size is that scale in percent,
// Left / right and Up / down a nudge in canvas px; the page moves the holes
// the way the picture moved (public/overlay/in_match.html, applyFit).
//
// Running the match (users.start_match_enabled): the match control
// (MatchControl: Call, Start, games, winner, DQ, reset) round the set on the
// overlay, and a switch (state.match.autoStart) that starts the next one
// when a match ends. Both act with the account's start.gg token, which the
// admin adds (Admin → user → start.gg token) and nothing shows back; the
// card only says whether there is one. The switch acts on the In-Match
// overlay's poll, not here, so it works with the console closed
// (functions/_lib/startMatch.js). The bracket page (/:slug/bracket) has the
// same control for any set.

// The roster as the overlays name it (art slug = value), the winner screen's
// list (migrations 0030 and 0033).
const CHARACTERS = [
  ['alisa', 'Alisa'], ['anna', 'Anna'], ['armor-king', 'Armor King'], ['asuka', 'Asuka'],
  ['azucena', 'Azucena'], ['bob', 'Bob'], ['bryan', 'Bryan'], ['claudio', 'Claudio'],
  ['clive', 'Clive'], ['devil-jin', 'Devil Jin'], ['dragunov', 'Dragunov'], ['eddy', 'Eddy'],
  ['fahkumram', 'Fahkumram'], ['feng', 'Feng'], ['heihachi', 'Heihachi'], ['hwoarang', 'Hwoarang'],
  ['jack-8', 'Jack-8'], ['jin', 'Jin'], ['jun', 'Jun'], ['kazuya', 'Kazuya'], ['king', 'King'],
  ['kuma', 'Kuma'], ['kunimitsu', 'Kunimitsu'], ['lars', 'Lars'], ['law', 'Law'], ['lee', 'Lee'],
  ['leo', 'Leo'], ['leroy', 'Leroy'], ['lidia', 'Lidia'], ['lili', 'Lili'], ['miary-zo', 'Miary Zo'],
  ['nina', 'Nina'], ['panda', 'Panda'], ['paul', 'Paul'], ['raven', 'Raven'], ['reina', 'Reina'],
  ['shaheen', 'Shaheen'], ['steve', 'Steve'], ['victor', 'Victor'], ['xiaoyu', 'Xiaoyu'],
  ['yoshimitsu', 'Yoshimitsu'], ['zafina', 'Zafina'],
];
const CHARACTER_LABEL = new Map(CHARACTERS);

// The characters with an outline (scripts/build-tekken8-hud-masks.mjs): the
// rest keep the whole label, so the picker says so.
const OUTLINES_URL = '/overlay/images/tekken-8/hud/index.json';

// The set on the overlay only matters when a pick or a start is made, and
// this read shares start.gg's rate limit with every overlay poller: every
// 10 s is plenty. The picker's lists change as sets start and finish; they
// are re-read when the picker is opened and every minute meanwhile.
const LIVE_POLL_MS = 10_000;
const OPTIONS_POLL_MS = 60_000;
// Event sets come 50 a page; four pages cover any bracket an operator would
// scroll, and a name filter narrows the rest.
const EVENT_PAGES_MAX = 4;
// A state read that started this soon after a pick may predate its save.
const PICK_SETTLE_MS = 2_000;

// start.gg set states: 2 in progress, 3 completed, 6 called.
const IN_PROGRESS = 2;
const COMPLETED = 3;
const CALLED = 6;

function setLabel(row) {
  const bits = [row.round, row.players.join(' vs ')];
  if (row.pool) bits.push(`Pool ${row.pool}`);
  if (row.state === IN_PROGRESS) bits.push('in progress');
  else if (row.state === CALLED) bits.push('called');
  return bits.filter(Boolean).join(' · ');
}

function stateWord(state) {
  if (state === COMPLETED) return 'over';
  if (state === IN_PROGRESS) return 'in progress';
  if (state === CALLED) return 'called';
  return 'not started';
}

export default function InMatchCard({
  name, packageSlug, tournamentSlug, stream, events,
  pick, onPick,
  showPicker, characters, onCharacters,
  hudFit, onHudFit,
  canStart, autoStart, onAutoStart,
}) {
  // undefined until the first read; null when nothing is on the overlay.
  const [live, setLive] = useState(undefined);
  const [outlined, setOutlined] = useState(null);
  // { saved, account } once read; the token itself never comes back.
  const [token, setToken] = useState(undefined);
  // The picker's lists: { stream, queue } from the endpoint, and the
  // chosen event's sets.
  const [options, setOptions] = useState(null);
  const [eventId, setEventId] = useState('');
  const [eventSets, setEventSets] = useState([]);
  const [eventName, setEventName] = useState('');
  const [eventLoading, setEventLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const pickRef = useRef(pick);
  const onPickRef = useRef(onPick);
  const pickedAtRef = useRef(0);
  pickRef.current = pick;
  onPickRef.current = onPick;

  useEffect(() => {
    if (!showPicker) return undefined;
    let cancelled = false;
    fetch(OUTLINES_URL)
      .then((r) => (r.ok ? r.json() : []))
      .then((slugs) => { if (!cancelled) setOutlined(new Set(Array.isArray(slugs) ? slugs : [])); })
      .catch(() => { if (!cancelled) setOutlined(new Set()); });
    return () => { cancelled = true; };
  }, [showPicker]);

  useEffect(() => {
    if (!canStart) return undefined;
    let cancelled = false;
    api.startggTokenStatus()
      .then((t) => { if (!cancelled) setToken(t); })
      .catch(() => { if (!cancelled) setToken({ saved: false, account: null }); });
    return () => { cancelled = true; };
  }, [canStart]);

  useEffect(() => {
    let cancelled = false;
    const read = () => {
      if (document.visibilityState === 'hidden') return;
      const startedAt = Date.now();
      api.packageGetState(packageSlug, { tournamentSlug, stream })
        .then((s) => {
          if (cancelled) return;
          setLive(s?.live?.setId ? s.live : null);
          // A pick the server no longer honours (the set over, or unknown)
          // goes back to Auto — judged only by a read made after the pick.
          if (pickRef.current && s?.live?.picked !== true && startedAt > pickedAtRef.current + PICK_SETTLE_MS) {
            onPickRef.current('');
          }
        })
        .catch(() => { if (!cancelled) setLive((prev) => prev ?? null); });
    };
    read();
    const timer = setInterval(read, LIVE_POLL_MS);
    document.addEventListener('visibilitychange', read);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', read);
    };
  }, [packageSlug, tournamentSlug, stream]);

  const loadOptions = () => {
    if (document.visibilityState === 'hidden') return;
    api.packageOnStream(packageSlug)
      .then((o) => setOptions(o))
      .catch(() => setOptions((prev) => prev ?? { stream: null, queue: [] }));
  };

  useEffect(() => {
    loadOptions();
    const timer = setInterval(loadOptions, OPTIONS_POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageSlug]);

  // The event the picker lists: the one on the stream queue, else the only
  // event, else the first.
  useEffect(() => {
    if (eventId || !options) return;
    const fromQueue = options.queue?.find((r) => r.eventId)?.eventId;
    const first = events?.[0]?.id != null ? String(events[0].id) : '';
    setEventId(fromQueue || first);
  }, [options, events, eventId]);

  useEffect(() => {
    if (!eventId) { setEventSets([]); setEventName(''); return undefined; }
    let cancelled = false;
    setEventLoading(true);
    (async () => {
      const rows = [];
      let pages = 1;
      try {
        for (let page = 1; page <= pages && page <= EVENT_PAGES_MAX; page++) {
          const r = await api.packageOnStream(packageSlug, { eventId, page });
          if (cancelled) return;
          rows.push(...(r.event?.sets ?? []));
          pages = r.event?.pagination?.totalPages ?? 1;
          if (r.event?.name) setEventName(r.event.name);
        }
      } catch { /* the queue's sets still list */ }
      if (!cancelled) { setEventSets(rows); setEventLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [packageSlug, eventId]);

  const forThisSet = !!live?.setId && characters?.setId === live.setId;
  const value = (side) => (forThisSet ? characters?.[side] ?? '' : '');
  const swapped = forThisSet && characters?.swapped === true;

  // A fit field keeps what is typed (an empty box stays empty while the
  // number is retyped); the endpoint and the page fall back to the default
  // for anything that isn't a number and clamp the rest.
  const FIT_DEFAULT = { size: 100, x: 0, y: 0 };
  const fitField = (key, label, min, max, step) => (
    <div className="col-4">
      <label className="form-label small mb-1" htmlFor={`in-match-fit-${key}`}>{label}</label>
      <input
        id={`in-match-fit-${key}`}
        type="number"
        className="form-control form-control-sm"
        min={min}
        max={max}
        step={step}
        value={hudFit?.[key] ?? FIT_DEFAULT[key]}
        onChange={(e) => {
          const raw = e.target.value;
          const next = raw === '' ? '' : Number(raw);
          onHudFit({ ...FIT_DEFAULT, ...(hudFit ?? {}), [key]: Number.isFinite(next) ? next : '' });
        }}
      />
    </div>
  );

  const setCharacters = (patch) => {
    if (!live?.setId) return;
    onCharacters({ left: value('left'), right: value('right'), swapped, setId: live.setId, ...patch });
  };

  const choose = (setId) => {
    pickedAtRef.current = Date.now();
    onPick(setId);
  };

  // The character each player has, by start.gg's order (swapped when the
  // players sit the other way round).
  const characterOf = (side) => {
    const slug = (side === 1) === !swapped ? value('left') : value('right');
    return slug ? CHARACTER_LABEL.get(slug) ?? slug : null;
  };
  const playerName = (side) => (side === 1 ? live?.player1 : live?.player2) || 'TBD';
  // start.gg's last reported character for the player on a seat (its order
  // is entrant 1, 2; the seats follow the swapped checkbox).
  const autoCharacter = (side) => {
    const auto = live?.characters ?? [];
    const slug = (side === 1) === !swapped ? auto[0] : auto[1];
    return slug ? CHARACTER_LABEL.get(slug) ?? slug : '—';
  };

  const queue = options?.queue ?? [];
  const queueIds = new Set(queue.map((r) => r.id));
  const needle = filter.trim().toLowerCase();
  const eventRows = eventSets.filter((r) =>
    !queueIds.has(r.id) && (!needle || r.id === pick || setLabel(r).toLowerCase().includes(needle)));
  const pickListed = !pick || queueIds.has(pick) || eventSets.some((r) => r.id === pick);

  const picker = (side, label) => (
    <div className="col-6">
      <label className="form-label small" htmlFor={`in-match-${side}`}>{label}</label>
      <CharacterPicker
        id={`in-match-${side}`}
        label={label}
        value={value(side)}
        options={CHARACTERS}
        outlined={outlined}
        disabled={!live?.setId}
        align={side === 'left' ? 'start' : 'end'}
        onChange={(slug) => setCharacters({ [side]: slug })}
      />
    </div>
  );

  return (
    <div className="card card-fui overlay-card">
      <div className="card-header d-flex align-items-center justify-content-between gap-2">
        <h6 className="mb-0">
          <img src="/images/section-icon.png" className="pp-icon" alt="" aria-hidden="true" /> {name}
        </h6>
        <span className="overlay-live-badge">Live</span>
      </div>
      <div className="card-body">
        <OverlayPreview slug="in-match" packageSlug={packageSlug} />

        <div className="row g-2 mb-2">
          {events?.length > 1 && (
            <div className="col-4">
              <label className="form-label small" htmlFor="in-match-event">Event</label>
              <select
                id="in-match-event"
                className="form-select form-select-sm"
                value={eventId}
                onChange={(e) => { setEventId(e.target.value); setFilter(''); }}
              >
                {events.map((ev) => (
                  <option key={ev.id} value={String(ev.id)}>{ev.name}</option>
                ))}
              </select>
            </div>
          )}
          <div className={events?.length > 1 ? 'col-8' : 'col-12'}>
            <label className="form-label small" htmlFor="in-match-pick">Show on overlay</label>
            <select
              id="in-match-pick"
              className="form-select form-select-sm"
              value={pick}
              onFocus={loadOptions}
              onChange={(e) => choose(e.target.value)}
            >
              <option value="">Auto — start.gg&apos;s stream queue</option>
              {queue.length > 0 && (
                <optgroup label={`On your stream${options?.stream ? ` (${options.stream})` : ''}`}>
                  {queue.map((r) => <option key={r.id} value={r.id}>{setLabel(r)}</option>)}
                </optgroup>
              )}
              {eventRows.length > 0 && (
                <optgroup label={eventName || 'Event'}>
                  {eventRows.map((r) => <option key={r.id} value={r.id}>{setLabel(r)}</option>)}
                </optgroup>
              )}
              {!pickListed && <option value={pick}>Set {pick}</option>}
            </select>
          </div>
        </div>
        {eventSets.length > 12 && (
          <input
            type="search"
            className="form-control form-control-sm mb-2"
            placeholder="Find a set by player or round…"
            aria-label="Find a set"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        )}
        {eventLoading && <p className="text-muted small mb-2">Loading the event&apos;s sets…</p>}

        <p className="small mb-2">
          {live === undefined && <span className="text-muted">Looking for the match on your stream…</span>}
          {live === null && (
            <span className="text-muted">
              {pick ? 'Your pick is over or unknown to start.gg.' : 'No match on your stream queue yet.'}
            </span>
          )}
          {live && (
            <>
              On the overlay: <strong>{live.player1 || 'TBD'}</strong>
              {' '}<span className="text-muted">{live.score1 ?? '0'}–{live.score2 ?? '0'}</span>{' '}
              <strong>{live.player2 || 'TBD'}</strong>
              {live.roundText ? <span className="text-muted"> · {live.roundText}</span> : null}
              {canStart && <span className="text-muted"> · {stateWord(live.state)}</span>}
              <span className="text-muted">{live.picked ? ' · your pick' : ' · from the stream queue'}</span>
            </>
          )}
          {' · '}
          <Link to={`/${packageSlug}/bracket`}>Open the bracket →</Link>
        </p>

        {canStart && live && (
          <MatchControl
            packageSlug={packageSlug}
            setId={live.setId}
            enabled={!!token?.saved}
            characterNames={[characterOf(1), characterOf(2)]}
            onMatch={(m) => setLive((prev) => (prev?.setId === m.setId ? { ...prev, state: m.state } : prev))}
          />
        )}

        {showPicker && (
          <>
            <div className="row g-2 mb-2">
              {picker('left', 'Left portrait (1P)')}
              {picker('right', 'Right portrait (2P)')}
            </div>
            {canStart && (
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="in-match-swapped"
                  checked={swapped}
                  disabled={!live?.setId}
                  onChange={(e) => setCharacters({ swapped: e.target.checked })}
                />
                <label className="form-check-label small" htmlFor="in-match-swapped">
                  Players sit the other way round ({playerName(2)} is 1P)
                </label>
              </div>
            )}
            <p className="text-muted small mb-0">
              Pick the characters in the game&apos;s top corners and the labels run on
              under their portraits. The picks are for this match only: the next one
              starts with the labels as usual.
              {canStart ? ' They also go into each game reported to start.gg.' : ''}
            </p>
            {live?.characters?.some(Boolean) && (
              <p className="text-muted small mb-0">
                From start.gg&apos;s reported games: left {autoCharacter(1)} · right {autoCharacter(2)},
                used for a side with no pick.
              </p>
            )}
            <div className="row g-2 align-items-end mt-2">
              <div className="col-12 small fw-semibold">HUD fit</div>
              {fitField('size', 'Size %', 90, 110, 0.5)}
              {fitField('x', 'Left / right', -60, 60, 1)}
              {fitField('y', 'Up / down', -60, 60, 1)}
            </div>
            <p className="text-muted small mb-0">
              Where your capture draws the game&apos;s portraits. 100 / 0 / 0 is a game that
              fills the canvas exactly. A game picture scaled down and centred in OBS wants
              its size here (97 when it covers 97% of the canvas), then nudge until the
              holes sit on the portraits. Shows within a few seconds.
            </p>
          </>
        )}

        {canStart && (
          <div className={showPicker ? 'mt-3 pt-3 border-top' : ''}>
            <div className="form-check form-switch">
              <input
                className="form-check-input"
                type="checkbox"
                role="switch"
                id="in-match-auto-start"
                checked={autoStart === true}
                disabled={!token?.saved}
                onChange={(e) => onAutoStart(e.target.checked)}
              />
              <label className="form-check-label small" htmlFor="in-match-auto-start">
                Start the next match automatically when one ends
              </label>
            </div>
            <p className="text-muted small mb-2">
              The next match on your stream queue, as soon as start.gg has the last one
              reported. Works while the In-Match overlay is open in OBS.
            </p>
            {token?.saved && (
              <p className="small mb-0">
                start.gg token: <strong>{token.account || 'saved'}</strong>
                <span className="text-muted"> (added by the admin)</span>
              </p>
            )}
            {token && !token.saved && (
              <p className="small text-muted mb-0">
                No start.gg token for this account yet: the admin adds it (Admin → user → start.gg token).
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
